<script setup>
import { Angle, Arc, Circle, Graph, Line, Point, Polygon, PolyLine, Sector, Vector } from '@ksassnowski/vueclid';
import { computed } from 'vue';

const props = defineProps({
    elements: {
        type: Array,
        required: true,
    },
    graph: {
        type: Object,
        required: false,
    },
});

const colors = [
    '#D32F2F',
    '#1976D2',
    '#388E3C',
    '#616161',
    '#F57C00',
    '#00BCD4',
    '#8E24AA',
    '#E91E63',
    '#F2CA19',
    '#B0BEC5',
];

const figures = computed(() => {
    return props.elements.map((e, index) => {
        return {
            ...e,
            color: e.color ? e.color : colors[index % colors.length],
            id: index,
        };
    });
});
</script>

<template>
    <div>
        <Graph :domain-x="graph?.domainX" :domain-y="graph?.domainY" :origin="graph?.origin" :grid="graph?.grid">
            <Point
                v-for="point in figures.filter((e) => e.type === 'point')"
                :key="point.id"
                :position="point.position"
                :color="point.color"
                :label="point.label"
                :filled="point.filled"
                :line-width="point.lineWidth"
                :radius="point.radius"
            />
            <Line
                v-for="segment in figures.filter((e) => e.type === 'segment')"
                :key="segment.id"
                :from="segment.from"
                :to="segment.to"
                :color="segment.color"
                :label="segment.label"
                :dashed="segment.dashed"
                :line-width="segment.lineWidth"
            />
            <Line
                v-for="line in figures.filter((e) => e.type === 'line')"
                :key="line.id"
                :slope="line.slope"
                :y-intercept="line.yIntercept"
                :color="line.color"
                :label="line.label"
                :dashed="line.dashed"
                :line-width="line.lineWidth"
            />
            <Vector
                v-for="vector in figures.filter((e) => e.type === 'vector')"
                :key="vector.id"
                :from="vector.from"
                :to="vector.to"
                :color="vector.color"
                :label="vector.label"
                :dashed="vector.dashed"
                :line-width="vector.lineWidth"
            />
            <Circle
                v-for="circle in figures.filter((e) => e.type === 'circle')"
                :key="circle.id"
                :position="circle.position"
                :radius="circle.radius"
                :color="circle.color"
                :label="circle.label"
                :filled="circle.filled"
                :dashed="circle.dashed"
                :line-width="circle.lineWidth"
            />
            <Arc
                v-for="arc in figures.filter((e) => e.type === 'arc')"
                :key="arc.id"
                :position="arc.position"
                :radius="arc.radius"
                :color="arc.color"
                :label="arc.label"
                :from="arc.from"
                :to="arc.to"
                :filled="arc.filled"
                :dashed="arc.dashed"
                :line-width="arc.lineWidth"
            />
            <Sector
                v-for="sector in figures.filter((e) => e.type === 'sector')"
                :key="sector.id"
                :position="sector.position"
                :radius="sector.radius"
                :color="sector.color"
                :label="sector.label"
                :from="sector.from"
                :to="sector.to"
                :filled="sector.filled"
                :dashed="sector.dashed"
                :line-width="sector.lineWidth"
            />
            <Polygon
                v-for="polygon in figures.filter((e) => e.type === 'polygon')"
                :key="polygon.id"
                :vertices="polygon.vertices"
                :color="polygon.color"
                :fill="polygon.fill"
                :angles="polygon.angles"
                :dashed="polygon.dashed"
                :line-width="polygon.lineWidth"
            />
            <Angle
                v-for="angle in figures.filter((e) => e.type === 'angle')"
                :key="angle.id"
                :a="angle.a"
                :b="angle.b"
                :c="angle.c"
                :radius="angle.radius"
                :color="angle.color"
                :label="angle.label"
                :filled="angle.filled"
                :dashed="angle.dashed"
                :line-width="angle.lineWidth"
            />
            <PolyLine
                v-for="polyline in figures.filter((e) => e.type === 'polyline')"
                :key="polyline.id"
                :points="polyline.points"
                :color="polyline.color"
                :filled="polyline.filled"
                :dashed="polyline.dashed"
                :line-width="polyline.lineWidth"
            />
        </Graph>
    </div>
</template>
